import React from 'react'
import { Box, Chip, LinearProgress, Paper, Typography } from '@mui/material'

import { AppCardTitle, AppEmptyLine } from '@components'
import { dsColorNew } from '@palette'
import { MbDelta } from 'Pages/MyBusiness/_shared/Ui'

/**
 * Compact "share" list — used for breakdowns like "by Office" or
 * "by Category" in the Overview.
 *
 * Props:
 *   title           — section header
 *   rows            — array of records
 *   labelOf(r)      — short tag shown on the left (e.g. office code)
 *   subOf(r)        — secondary label (e.g. office name)
 *   valueFormatter  — formats the value for display (e.g. fmtEur)
 *   valueKey        — which field to read (default 'revenue')
 *   keyField        — unique field per row (e.g. 'code', 'sipp_code');
 *                     required when onSelect is provided
 *   selectedKey     — when set, that row is highlighted and the rest
 *                     fade. Use this to reflect a cross-filter
 *                     selection driven from outside.
 *   onSelect(key)   — click handler. Receives the row's key, or null
 *                     if the user clicked the already-selected row to
 *                     clear the selection.
 *
 * Compare bands: if a row carries `revenue_compare` and `delta_pct`,
 * a green/red chip is rendered next to the value.
 */
const fmtPct = (n) =>
	new Intl.NumberFormat('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
		.format(n) + ' %'

// Default cap on the visible rows area so a 15-row breakdown doesn't
// stretch the dashboard a full screen. Bigger than this and we paginate
// via overflow-y scrolling. Roughly 8 rows fit at the current row height.
const DEFAULT_BODY_MAX_HEIGHT = 420

// The icon of the card title, from what the rows are
const ICON_BY_KEY = { code: 'location-dot', sipp_code: 'car', country_code: 'globe', channel_type: 'shuffle', partner_code: 'handshake' }

export default function ShareList({
	title,
	rows,
	labelOf,
	subOf,
	valueFormatter,
	valueKey = 'revenue',
	keyField,
	selectedKey,
	onSelect,
	maxBodyHeight = DEFAULT_BODY_MAX_HEIGHT,
	icon,
}) {
	const titleIcon = icon || ICON_BY_KEY[keyField] || 'chart-simple'
	if (!rows || !rows.length) {
		return (
			<Paper sx={{ p: 2.5 }}>
				<AppCardTitle icon={titleIcon} title={title}/>
				<AppEmptyLine>No data in this date range.</AppEmptyLine>
			</Paper>
		)
	}

	const max   = Math.max(...rows.map(r => r[valueKey] || 0)) || 1
	// Share % recomputed locally over whichever metric the host picked,
	// so swapping metrics from the KPI banner doesn't need a refetch.
	const total = rows.reduce((s, r) => s + (r[valueKey] || 0), 0)
	const clickable = typeof onSelect === 'function' && keyField

	const handleClick = (row) => {
		if (!clickable) return
		const k = row[keyField]
		onSelect(k === selectedKey ? null : k)
	}

	return (
		<Paper sx={{ p: 2.5 }}>
			<Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
				<AppCardTitle icon={titleIcon} title={title}/>
				{clickable && selectedKey != null && (
					<Chip size="small" label="Clear filter" onClick={() => onSelect(null)} variant="outlined"/>
				)}
			</Box>

			<Box display="flex" flexDirection="column" gap={0.25}
			     sx={{
				     maxHeight: maxBodyHeight,
				     overflowY: 'auto',
				     // Reserve room for the scrollbar so contents don't reflow
				     // when it appears/disappears.
				     pr: 0.5,
			     }}>
				{rows.length > 8 && (
					<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, position: 'sticky', top: 0,
					                  bgcolor: dsColorNew.white, zIndex: 1, py: 0.25 }}>
						Scroll for {rows.length - 8} more
					</Typography>
				)}
				{rows.map((r, i) => {
					const value      = r[valueKey] || 0
					const pct        = (value / max) * 100
					const sharePct   = total ? (value / total) * 100 : 0
					const isSelected = clickable && r[keyField] === selectedKey
					const dimmed     = clickable && selectedKey != null && !isSelected
					const hasDelta   = r.delta_pct != null

					return (
						<Box key={i} onClick={() => handleClick(r)}
						     sx={{
							     display: 'grid',
							     gridTemplateColumns: 'auto minmax(0, 1fr) minmax(60px, 1fr) auto auto auto',
							     gap: 1.5,
							     alignItems: 'center',
							     px: 1, py: 0.9,
							     borderRadius: '8px',
							     opacity: dimmed ? 0.4 : 1,
							     cursor: clickable ? 'pointer' : 'default',
							     transition: 'opacity 150ms ease, background-color 150ms ease',
							     '&:hover': clickable ? { bgcolor: isSelected ? dsColorNew.pillActiveBg : dsColorNew.hoverBgLight } : {},
							     ...(isSelected && { bgcolor: dsColorNew.pillActiveBg, boxShadow: `inset 3px 0 0 ${dsColorNew.appBlue}` }),
						     }}>
							<Typography sx={{ fontWeight: 700, fontSize: 13, color: dsColorNew.textTitle, minWidth: 56 }}>
								{labelOf(r)}
							</Typography>
							<Typography noWrap
							            sx={{ fontSize: 13, color: dsColorNew.textMuted }}
							            title={subOf?.(r) || ''}>
								{subOf?.(r) || ''}
							</Typography>
							<LinearProgress variant="determinate" value={pct}/>
							<Typography sx={{ textAlign: 'right', minWidth: 96, fontVariantNumeric: 'tabular-nums',
							                  fontSize: 13, fontWeight: 600, color: dsColorNew.textTitle }}>
								{valueFormatter(value)}
							</Typography>
							<Box sx={{ minWidth: 72, display: 'flex', justifyContent: 'flex-end' }}>
								{hasDelta && <MbDelta value={r.delta_pct}/>}
							</Box>
							<Typography sx={{ textAlign: 'right', minWidth: 52, fontSize: 12.5, color: dsColorNew.textMuted, fontVariantNumeric: 'tabular-nums' }}>
								{fmtPct(sharePct)}
							</Typography>
						</Box>
					)
				})}
			</Box>
		</Paper>
	)
}
